import { AfterViewInit, Component, Input, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
// Use relative imports within the same library (libs/shared-lib-platform)
import { ConfigService } from '../../shared-services/config.service';
import { CommonModule } from '@angular/common';

import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap, tap, takeUntil } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PageEvent } from '@angular/material/paginator';
// use shared ConfigService from library
import { HttpClient } from '@angular/common/http';
import { PaginatorComponent } from '../../global-components/paginator/paginator.component';

// Import utility functions
import {
  matchValue,
  findMatchingCategories,
  buildCategoryFilterData,
  type MatchMode,
} from './../../utils/filter.utils';

import { paginate } from './../../utils/data-transform.utils';
import {
  isFrozenColumn as _isFrozenColumn,
  isLastFrozenColumn as _isLastFrozenColumn,
  isFirstFrozenColumn as _isFirstFrozenColumn,
  getFrozenColumnLeft as _getFrozenColumnLeft,
  isFrozenGroup as _isFrozenGroup,
  isFirstFrozenGroup as _isFirstFrozenGroup,
  getFrozenGroupLeft as _getFrozenGroupLeft,
  isFrozenBodyRow as _isFrozenBodyRow,
  getFrozenBodyRowTop as _getFrozenBodyRowTop,
  getFrozenBodyRowBottom as _getFrozenBodyRowBottom,
} from './../../utils/table-freeze.utils';
import { formatDisplayText } from './../../utils/text-case.utils';

type TableHeaderGroupInput = {
  key?: string;
  label?: string;
  columns?: Array<string | { key?: string; label?: string }>;
  children?: TableHeaderGroupInput[];
  className?: string;
};

type TableHeaderGroupNode = {
  id: string;
  key?: string;
  label: string;
  className?: string;
  columns: string[];
  children: TableHeaderGroupNode[];
};

type TableHeaderCell = {
  id: string;
  groupId: string;
  label: string;
  className?: string;
  colspan: number;
  rowspan: number;
  depth: number;
  columns: string[];
  frozen: boolean;
  leftOffset: number | null;
  hasChildren: boolean;
};

@Component({
  selector: 'generic-table',
  standalone: true,
  imports: [MatTableModule, CommonModule, MatProgressSpinnerModule, PaginatorComponent],
  templateUrl: './generic-table.html',
  styleUrl: './generic-table.scss',
})
export class GenericTableComponent implements AfterViewInit, OnDestroy {
  private readonly documentMouseMoveHandler = (event: MouseEvent): void => {
    this.onMouseMove(event);
  };

  private readonly documentMouseUpHandler = (): void => {
    this.onMouseUp();
  };

  @Input() data!: any;
  displayedColumns: string[] = [];
  columnWidths: { [col: string]: number } = {};
  protected readonly DEFAULT_COL_WIDTH: number = 100;
  protected readonly MIN_COL_WIDTH: number = 16;
  protected resizingCol: string | null = null;
  protected resizeStartX = 0;
  protected resizeStartWidth = 0;
  protected suppressNextHeaderClick = false;
  filter: any;
  isLoading = true;
  columnLabels: { [key: string]: string } = {};
  dataSource: any[] = [];
  protected unSubscribe$: Subject<void> = new Subject();
  @Input() set widgetId(val: string) {
    this._widgetId = val;
  }
  get widgetId(): string {
    return this._widgetId || this.data?.widgetId;
  }
  protected _widgetId!: string;

  get tableHeight(): string {
    const configured =
      this.data?.heightContainer || this.data?.height || this.data?.chartConfig?.height;

    if (typeof configured === 'number') {
      return `${configured}px`;
    }

    if (typeof configured === 'string' && configured.trim()) {
      const normalized = configured.trim().toLowerCase();
      if (normalized.endsWith('vh') || normalized.endsWith('vw') || normalized.endsWith('%')) {
        return '100%';
      }
      return configured;
    }

    return 'auto';
  }

  get hasImpactDriversData(): boolean {
    return this.dataSource.some(
      (row: any) => row?.option_impact_drivers && String(row.option_impact_drivers).trim() !== '',
    );
  }

  pagedData: any[] = [];
  protected originalDataSource: any[] = [];
  activeLegend: string | null = null;

  protected pageSize!: number;
  protected pageIndex: number = 0;

  // Cell selection properties (supports multiple selection with Ctrl+click)
  selectedCells: { row: any; column: string }[] = [];

  // Grouped header properties
  headerGroups: TableHeaderGroupNode[] = [];
  headerGroupRows: TableHeaderCell[][] = [];
  protected headerGroupNodeMap = new Map<string, TableHeaderGroupNode>();
  groupHeaderDisplayedColumns: string[] = [];
  hasGroupedHeaders = false;

  // Column collapse/expand properties
  collapsedColumns = new Set<string>();
  collapsedGroups = new Set<string>();
  private preCollapseWidths: { [col: string]: number } = {};
  protected readonly DEFAULT_COLLAPSED_COL_WIDTH = 24;

  // Header selection properties (for clickable headers)
  selectedGroupHeaders: Set<string> = new Set();
  selectedColumnHeaders: Set<string> = new Set();

  // ─── Freeze Row / Column (delegated to shared utility) ─────────────────────

  // Cross-filtering properties
  protected legendFilterSub!: Subscription;
  // Stores the last active legendSync filter so it can be re-applied after a data reload
  protected activeLegendSyncFilter: {
    sourceWidgetId: string;
    selectedLegends: string[];
    selectedIndices: number[];
    selectedColors: string[];
    targetWidgetIds: string[];
    visitedWidgetIds: string[];
    isCategoryFilter: boolean;
  } | null = null;

  @ViewChild('paginatorComp') paginatorComp!: PaginatorComponent;

  public isQuarterlyIdentifierColumn(column: string): boolean {
    return (
      this.data?.enableQuarterlyPivot === true &&
      (column === 'customer_name' || column === 'channel_type')
    );
  }

  public isQuarterlyIdentifierGroup(group: { columns?: string[] } | null | undefined): boolean {
    if (this.data?.enableQuarterlyPivot !== true) return false;
    const cols = group?.columns || [];
    return cols.length === 1 && this.isQuarterlyIdentifierColumn(cols[0]);
  }

  public isSingleQuarterYearGroup(
    group: { label?: string; columns?: string[] } | null | undefined,
  ): boolean {
    if (this.data?.enableQuarterlyPivot !== true) return false;
    const label = String(group?.label || '');
    const cols = group?.columns || [];
    return /^\d{4}$/.test(label) && cols.length === 1;
  }

  public isFirstFrozenColumn(column: string): boolean {
    return _isFirstFrozenColumn(this.data, this.displayedColumns, column);
  }

  public getGroupWidthPx(group: { columns?: string[] } | null | undefined): number {
    const cols = group?.columns || [];
    if (!cols.length) return this.DEFAULT_COL_WIDTH;
    return cols.reduce(
      (sum, col) => sum + (this.columnWidths[col] ?? this.DEFAULT_COL_WIDTH),
      0,
    );
  }

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
    private http: HttpClient
  ) {
    this.legendFilterSub = this.globalFilterService.filter$.subscribe((filters: any) => {
      // Skip if data not yet available
      if (!this.data?.widgetId) return;

      // Check if this widget should respond to the filter
      if (this.chartFilterHandler.shouldReload(filters, this.data.widgetId)) {
        if (filters?.legendSync) {
          this.handleLegendSync(filters);
          // For category filters (label clicks), also trigger API reload
          if (filters.isCategoryFilter) {
            this.loadAndRenderChart(filters);
          }
        } else {
          console.log('filters 22', filters);
          this.loadAndRenderChart(filters);
        }
      }
    });
  }

  protected resolveFilterListeners(source: { filterListener?: string | string[]; filterListner?: string | string[] } | null | undefined): string[] {
    const listener = source?.filterListener ?? source?.filterListner;

    if (!listener) {
      return [];
    }

    const listeners = Array.isArray(listener) ? listener : String(listener).split(',');
    return listeners.map((s: string) => s.trim()).filter(Boolean);
  }

  // Renamed from subscribeToCrossFilterEvents logic
  protected handleLegendSync(filters: any): void {
    // Build event object for handleExternalLegendFilter
    const listeners = this.resolveFilterListeners(filters);

    // Get legend names from indices if not provided directly
    let selectedLegends = filters.selectedLegends || [];
    if (
      selectedLegends.length === 0 &&
      filters.selectedIndices?.length > 0 &&
      filters.seriesNames
    ) {
      selectedLegends = filters.selectedIndices
        .map((i: number) => filters.seriesNames[i])
        .filter(Boolean);
    }

    this.handleExternalLegendFilter({
      sourceWidgetId: filters.sourceWidgetId,
      selectedLegends: selectedLegends,
      selectedIndices: filters.selectedIndices || [],
      selectedColors: filters.selectedColors || [],
      targetWidgetIds: listeners,
      visitedWidgetIds: filters.visitedWidgetIds || [],
      isCategoryFilter: filters.isCategoryFilter || false,
    });
  }

  ngAfterViewInit(): void {
    // Initial load
    setTimeout(() => this.loadAndRenderChart(), 0);
  }

  // Initialize table with inline data (for POC/mock data)
  protected initTableWithData(tableData: any[]): void {
    this.isLoading = false;
    // Set pageSize from config if provided
    if (this.data?.pageSize) {
      this.pageSize = this.data.pageSize;
    }
    this.originalDataSource = tableData;
    this.dataSource = [...this.originalDataSource];

    if (!!this.data?.isPagination) {
      this.setPagedData();
    }

    this.displayedColumns = this.getDisplayedColumnsFromResponse(this.data, tableData);
    // Per UI Standards (Point 4): headers in Capital Case unless overridden via data.textCase
    const headerCase = (this.data?.textCase || 'capital') as any;
    const labelMap = this.getLabelMapFromResponse(this.data);
    this.columnLabels = Object.fromEntries(
      this.displayedColumns.map((key) => [
        key,
        labelMap.get(key) || formatDisplayText(key, headerCase),
      ]),
    );

    this.initColumnWidths();

    // Initialize grouped headers if configured
    this.initGroupedHeaders();

    setTimeout(() => {
      this.paginatorComp?.updatePagination(
        this.dataSource.length,
        this.pageIndex,
        // this.pageSize
      );
    }, 200);
  }

  protected getConfiguredHeaderGroupInputs(): TableHeaderGroupInput[] {
    const groups = this.data?.columnGroups || this.data?.headerGroups || [];
    return Array.isArray(groups) ? groups : [];
  }

  protected normalizeHeaderGroupNode(
    group: TableHeaderGroupInput,
    path: string,
  ): TableHeaderGroupNode | null {
    if (!group || typeof group !== 'object') return null;

    const directColumns = Array.isArray(group.columns)
      ? group.columns
          .map((col: any) => {
            if (typeof col === 'string') return col.trim();
            if (col && typeof col === 'object') return String(col.key || '').trim();
            return '';
          })
          .filter(Boolean)
      : [];

    const children = Array.isArray(group.children)
      ? group.children
          .map((child: TableHeaderGroupInput, index: number) =>
            this.normalizeHeaderGroupNode(child, `${path}-child-${index}`),
          )
          .filter((child): child is TableHeaderGroupNode => !!child)
      : [];

    const childColumns = children.flatMap((child) => child.columns);
    const columns = Array.from(new Set([...directColumns, ...childColumns]));
    if (columns.length === 0) return null;

    const label = String(group.label || group.key || '').trim();

    return {
      id: path,
      key: group.key,
      label: label || formatDisplayText(group.key || path, this.data?.textCase || 'capital'),
      className: group.className || '',
      columns,
      children,
    };
  }

  protected normalizeHeaderGroupTree(groups: TableHeaderGroupInput[]): TableHeaderGroupNode[] {
    return groups
      .map((group: TableHeaderGroupInput, index: number) =>
        this.normalizeHeaderGroupNode(group, `group-${index}`),
      )
      .filter((group): group is TableHeaderGroupNode => !!group);
  }

  protected collectHeaderGroupNodes(groups: TableHeaderGroupNode[]): TableHeaderGroupNode[] {
    const nodes: TableHeaderGroupNode[] = [];
    const visit = (node: TableHeaderGroupNode): void => {
      nodes.push(node);
      node.children.forEach((child: TableHeaderGroupNode) => visit(child));
    };
    groups.forEach(visit);
    return nodes;
  }

  protected getHeaderGroupDepth(node: TableHeaderGroupNode): number {
    if (!node.children?.length) return 1;
    return 1 + Math.max(...node.children.map((child) => this.getHeaderGroupDepth(child)));
  }

  protected buildHeaderGroupRows(groups: TableHeaderGroupNode[]): TableHeaderCell[][] {
    if (!Array.isArray(groups) || groups.length === 0) return [];

    const maxDepth = Math.max(...groups.map((group) => this.getHeaderGroupDepth(group)));
    const rows: TableHeaderCell[][] = Array.from({ length: maxDepth }, () => []);

    const visit = (node: TableHeaderGroupNode, depth: number): void => {
      const columns = node.columns || [];
      const frozen = _isFrozenGroup(this.data, this.displayedColumns, { columns });
      const leftOffset = frozen
        ? _getFrozenGroupLeft(this.data, this.displayedColumns, this.columnWidths, this.DEFAULT_COL_WIDTH, {
            columns,
          })
        : null;

      rows[depth].push({
        id: `${node.id}-${depth}`,
        groupId: node.id,
        label: node.label,
        className: node.className || '',
        colspan: Math.max(columns.length, 1),
        rowspan: node.children?.length ? 1 : maxDepth - depth,
        depth,
        columns,
        frozen,
        leftOffset,
        hasChildren: node.children.length > 0,
      });

      node.children.forEach((child) => visit(child, depth + 1));
    };

    groups.forEach((group) => visit(group, 0));
    return rows;
  }

  // Initialize grouped headers from config / response tree
  protected initGroupedHeaders(): void {
    const configuredGroups = this.getConfiguredHeaderGroupInputs();
    if (configuredGroups.length > 0) {
      this.hasGroupedHeaders = true;
      this.headerGroups = this.normalizeHeaderGroupTree(configuredGroups);
      this.headerGroupNodeMap = new Map(
        this.collectHeaderGroupNodes(this.headerGroups).map((node) => [node.id, node]),
      );
      this.headerGroupRows = this.buildHeaderGroupRows(this.headerGroups);
      this.groupHeaderDisplayedColumns = this.headerGroups.flatMap((g) => g.columns);
      console.log(
        '[Base GenericTableComponent] initGroupedHeaders - widgetId:',
        this.widgetId,
        'headerGroups:',
        this.headerGroups,
        'headerGroupRows:',
        this.headerGroupRows,
        'hasGroupedHeaders:',
        this.hasGroupedHeaders,
      );
    } else {
      this.hasGroupedHeaders = false;
      this.headerGroups = [];
      this.headerGroupRows = [];
      this.headerGroupNodeMap.clear();
      this.groupHeaderDisplayedColumns = [];
      console.log(
        '[Base GenericTableComponent] initGroupedHeaders - widgetId:',
        this.widgetId,
        'NO headerGroups configured. hasGroupedHeaders set to false.',
      );
    }
  }

  protected applyResponseTableConfig(res: any): void {
    if (!res || !this.data) return;

    const responseConfigKeys = [
      'freezeColumn',
      'freezeColumns',
      'freezeColumnCount',
      'freezeColumnIndexes',
      'freezeColumnWidth',
      'freezeColumnBg',
      'freezeColumnColor',
      'freezeFirstColumnBg',
      'freezeFirstColumnTextColor',
      'freezeColumnTextColor',
      'freezeRow',
      'freezeBodyRows',
      'freezeBodyRowIndexes',
      'freezeRows',
      'freezeBodyRowCount',
      'freezeBodyRowHeight',
      'freezeHeaderHeight',
      'headerColumns',
      'headerGroups',
      'columnGroups',
      'columnWidths',
      'columnFilterKeyMap',
      'textCase',
      'showColumnDividers',
      'dividerColumns',
      'labelKey',
      'legendKey',
      'doublexAxisKey',
    ] as const;

    responseConfigKeys.forEach((key) => {
      if (res[key] !== undefined && res[key] !== null) {
        this.data[key] = res[key];
      }
    });
  }

  protected getDisplayedColumnsFromResponse(response: any, rows: any[]): string[] {
    const groupColumns: string[] = [];
    const groupColumnSet = new Set<string>();

    const collectGroupColumns = (group: any): void => {
      if (!group || typeof group !== 'object') return;

      if (Array.isArray(group.columns)) {
        for (const col of group.columns) {
          const key = typeof col === 'string' ? col : col?.key;
          if (key && !groupColumnSet.has(key)) {
            groupColumnSet.add(key);
            groupColumns.push(key);
          }
        }
      }

      if (Array.isArray(group.children)) {
        group.children.forEach((child: any) => collectGroupColumns(child));
      }
    };

    const configuredGroups = response?.columnGroups || response?.headerGroups;
    if (Array.isArray(configuredGroups)) {
      configuredGroups.forEach((group: any) => collectGroupColumns(group));
    }

    const rowKeys = new Set<string>();
    if (Array.isArray(rows)) {
      rows.forEach((row: any) => {
        if (row && typeof row === 'object' && !Array.isArray(row)) {
          Object.keys(row).forEach((key: string) => {
            if (!groupColumnSet.has(key)) {
              rowKeys.add(key);
            }
          });
        }
      });
    }

    if (groupColumns.length > 0) {
      return [...groupColumns, ...Array.from(rowKeys)];
    }

    if (Array.isArray(response?.headerColumns)) {
      return response.headerColumns
        .map((col: any) => (typeof col === 'string' ? col : col?.key))
        .filter(Boolean);
    }

    return Array.from(rowKeys);
  }

  protected getLabelMapFromResponse(response: any): Map<string, string> {
    const labelMap = new Map<string, string>();

    if (Array.isArray(response?.headerColumns)) {
      response.headerColumns.forEach((col: any) => {
        const key = typeof col === 'string' ? col : col?.key;
        const label = typeof col === 'string' ? col : col?.label || col?.key;
        if (key) {
          labelMap.set(key, label);
        }
      });
    }

    const configuredGroups = response?.columnGroups || response?.headerGroups;
    if (Array.isArray(configuredGroups)) {
      const visit = (group: any): void => {
        if (!group || typeof group !== 'object') return;

        if (Array.isArray(group.columns)) {
          group.columns.forEach((col: any) => {
            const key = typeof col === 'string' ? col : col?.key;
            const label = typeof col === 'string' ? col : col?.label || col?.key;
            if (key && !labelMap.has(key)) {
              labelMap.set(key, label);
            }
          });
        }

        if (Array.isArray(group.children)) {
          group.children.forEach((child: any) => visit(child));
        }
      };

      configuredGroups.forEach((group: any) => visit(group));
    }

    return labelMap;
  }

  // Helper to check row equality by content if reference differs
  protected isSameRow(row1: any, row2: any): boolean {
    if (row1 === row2) return true;
    if (!row1 || !row2) return false;

    // Use keys from config or headers to identify uniqueness
    let keys = this.data?.filterKey;
    if (!keys || keys.length === 0) {
      keys = this.data?.headerColumns?.map((col: any) => col.key);
    }

    if (keys && keys.length > 0) {
      // Compare values for identification keys
      return keys.every((key: string) => {
        const val1 = row1[key] !== undefined && row1[key] !== null ? String(row1[key]).trim() : '';
        const val2 = row2[key] !== undefined && row2[key] !== null ? String(row2[key]).trim() : '';
        return val1 === val2;
      });
    }

    return false;
  }

  // Cell selection methods (supports Ctrl+click for multiple selection)
  onCellClick(row: any, column: string, event: MouseEvent): void {
    const cellIndex = this.selectedCells.findIndex(
      (cell) => this.isSameRow(cell.row, row) && cell.column === column,
    );
    const isAlreadySelected = cellIndex !== -1;

    if (event.ctrlKey || event.metaKey) {
      // Ctrl+click: toggle selection (add or remove from selection)
      if (isAlreadySelected) {
        this.selectedCells.splice(cellIndex, 1);
      } else {
        this.selectedCells.push({ row, column });
      }
    } else {
      // Normal click: single selection
      if (isAlreadySelected && this.selectedCells.length === 1) {
        // Deselect if clicking the only selected cell
        this.selectedCells = [];
      } else {
        // Select only this cell
        this.selectedCells = [{ row, column }];
      }
    }

    // Update filters based on ALL currently selected cells (multi-select aware)
    this.updateFilterFromSelectedCells();

    // Also emit legendSync reset to other tables when nothing is selected
    if (this.selectedCells.length === 0) {
      this.emitLegendFilter([]);
    }
  }

  // Update filter selection (aligned with StackBarChartComponent)
  protected updateFilterSelection(data: any): void {
    this.chartFilterHandler.updateFilterSelection(this.data, data);
  }

  /**
   * Rebuilds requestParams from the current selectedCells state.
   * Groups cell values by their API attribute key (via columnFilterKeyMap).
   * Supports multi-select: multiple cells for the same key accumulate into an array.
   * Clears keys from requestParams that are no longer in selectedCells.
   */
  protected updateFilterFromSelectedCells(): void {
    if (!this.data?.filterKey) return;

    const columnFilterKeyMap: { [col: string]: string } = this.data?.columnFilterKeyMap || {};
    const listeners = this.resolveFilterListeners(this.data);
    const listenerStr = listeners.join(',');
    const widgetId = this.data?.widgetId;

    // All possible API keys this table manages (for cleanup)
    const allFilterKeys: string[] = Array.isArray(this.data.filterKey)
      ? this.data.filterKey
      : [this.data.filterKey];

    if (this.selectedCells.length === 0) {
      // Clear all managed filter keys from requestParams
      allFilterKeys.forEach((key, index) => {
        const isLast = index === allFilterKeys.length - 1;
        if (isLast) {
          this.globalFilterService.resetFilterSelection(key, false);
        } else {
          this.globalFilterService.deleteKey(key, false);
        }
      });
      return;
    }

    // Build apiKey -> Set<value> from all selected cells
    const filterMap = new Map<string, Set<any>>();
    const identifierKeys: string[] = Array.isArray(this.data.filterKey)
      ? this.data.filterKey
      : [this.data.filterKey];
    // Respect configured total key (defaults to 'TOTAL') - do not include it as a filter attribute
    const totalKey = (this.data && this.data.totalKey) || 'TOTAL';

    for (const cell of this.selectedCells) {
      const apiKey = columnFilterKeyMap[cell.column] || cell.column;

      const isTotalColumn = apiKey === totalKey || cell.column === totalKey;
      if (!isTotalColumn) {
        if (!filterMap.has(apiKey)) {
          filterMap.set(apiKey, new Set());
        }
        // If the column maps to a DIFFERENT API key (e.g. "2019" -> "year"),
        // use the column name itself as the value ("2019"), not the cell data.
        // Otherwise use the actual cell value.
        const val = apiKey !== cell.column ? cell.column : cell.row[cell.column];
        if (val !== null && val !== undefined && val !== '') {
          filterMap.get(apiKey)!.add(val);
        }
      }
      // If the column maps to a DIFFERENT API key (e.g. "2019" -> "year"),
      // use the column name itself as the value ("2019"), not the cell data.
      // Otherwise use the actual cell value.
      // const val = (apiKey !== cell.column) ? cell.column : cell.row[cell.column];
      // if (val !== null && val !== undefined && val !== '') {
      //   filterMap.get(apiKey)!.add(val);
      // }
      // Also include identifier values from the selected row (e.g. customer_name, channel_type)
      // so that selecting a year cell will also pass row identifiers alongside the year.
      for (const idKey of identifierKeys) {
        // don't duplicate if idKey is the same as apiKey (already handled)
        if (idKey === apiKey) continue;
        const idVal = cell.row?.[idKey];
        if (idVal !== null && idVal !== undefined && idVal !== '') {
          if (!filterMap.has(idKey)) {
            filterMap.set(idKey, new Set());
          }
          filterMap.get(idKey)!.add(idVal);
        }
      }
    }

    // Clear keys no longer active
    allFilterKeys.forEach((key) => {
      if (!filterMap.has(key)) {
        this.globalFilterService.deleteKey(key, false);
      }
    });

    // Set each active key with its accumulated values
    const activeKeys = Array.from(filterMap.keys());
    activeKeys.forEach((key, index) => {
      const values = Array.from(filterMap.get(key)!);
      const isLast = index === activeKeys.length - 1;
      this.globalFilterService.updateFilterSelection(
        key,
        values,
        false,
        widgetId,
        isLast,
        listenerStr || undefined,
      );
    });
  }

  // Collect all filter values from selected cells (uses crossFilterColumns config)
  protected emitFilterFromSelectedCells(): void {
    const filterValues: string[] = [];
    // Get crossFilterColumns from config, default to empty array
    const crossFilterColumns: string[] = this.data?.crossFilterColumns || [];
    const identifierKeys: string[] = Array.isArray(this.data.filterKey)
      ? this.data.filterKey
      : [this.data.filterKey];

    this.selectedCells.forEach((cell) => {
      const row = cell.row;

      // Add values from all configured crossFilterColumns
      crossFilterColumns.forEach((colKey) => {
        const value = row[colKey];
        if (value && !filterValues.includes(value)) {
          filterValues.push(value);
        }
      });

      // Also add the clicked cell value if not already included
      const cellValue = row[cell.column];
      if (cellValue && !filterValues.includes(cellValue)) {
        filterValues.push(cellValue);
      }

      // Also include identifier values (customer_name, channel_type, etc.) so
      // cross-widget filters receive row identifiers along with the clicked cell.
      identifierKeys.forEach((idKey) => {
        const idVal = row?.[idKey];
        if (idVal && !filterValues.includes(idVal)) {
          filterValues.push(idVal);
        }
      });
    });

    this.emitLegendFilter(filterValues);
  }

  isCellSelected(row: any, column: string): boolean {
    return this.selectedCells.some(
      (cell) => this.isSameRow(cell.row, row) && cell.column === column,
    );
  }

  protected getHeaderGroupNode(groupId: string): TableHeaderGroupNode | undefined {
    return this.headerGroupNodeMap.get(groupId);
  }

  // Group header click handler (e.g., 2023, 2024, 2025)
  onGroupHeaderClick(
    group: TableHeaderCell,
    event: MouseEvent,
  ): void {
    if (!group?.groupId) return;

    if (event.ctrlKey || event.metaKey) {
      // Ctrl+click: toggle selection
      if (this.selectedGroupHeaders.has(group.groupId)) {
        this.selectedGroupHeaders.delete(group.groupId);
      } else {
        this.selectedGroupHeaders.add(group.groupId);
      }
    } else {
      // Normal click: single selection
      if (this.selectedGroupHeaders.size === 1 && this.selectedGroupHeaders.has(group.groupId)) {
        this.selectedGroupHeaders.clear();
      } else {
        this.selectedGroupHeaders.clear();
        this.selectedGroupHeaders.add(group.groupId);
      }
    }

    // Clear other selections
    this.selectedCells = [];
    this.selectedColumnHeaders.clear();

    // Emit filter with group label and all column keys in this group
    this.emitHeaderFilter();
  }

  isGroupHeaderSelected(groupId: string): boolean {
    return this.selectedGroupHeaders.has(groupId);
  }

  // Column header click handler (e.g., Q4, Q1, Q2)
  onColumnHeaderClick(column: string, event: MouseEvent): void {
    if (this.suppressNextHeaderClick) {
      this.suppressNextHeaderClick = false;
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      // Ctrl+click: toggle selection
      if (this.selectedColumnHeaders.has(column)) {
        this.selectedColumnHeaders.delete(column);
      } else {
        this.selectedColumnHeaders.add(column);
      }
    } else {
      // Normal click: single selection
      if (this.selectedColumnHeaders.size === 1 && this.selectedColumnHeaders.has(column)) {
        this.selectedColumnHeaders.clear();
      } else {
        this.selectedColumnHeaders.clear();
        this.selectedColumnHeaders.add(column);
      }
    }

    // Clear other selections
    this.selectedCells = [];
    this.selectedGroupHeaders.clear();

    // Emit filter with column label
    this.emitHeaderFilter();
  }

  isColumnHeaderSelected(column: string): boolean {
    return this.selectedColumnHeaders.has(column);
  }

  // Check if a column should be highlighted (either directly selected or part of a selected group)
  isColumnHighlighted(column: string): boolean {
    // Check if column is directly selected
    if (this.selectedColumnHeaders.has(column)) {
      return true;
    }

    // Check if column belongs to a selected group header
    for (const groupId of Array.from(this.selectedGroupHeaders)) {
      const group = this.getHeaderGroupNode(groupId);
      if (group && group.columns && group.columns.includes(column)) {
        return true;
      }
    }

    return false;
  }

  // ─── Column Collapse / Expand ──────────────────────────────────────────────

  toggleColumnCollapse(column: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.collapsedColumns.has(column)) {
      this.collapsedColumns.delete(column);
      this.columnWidths[column] = this.preCollapseWidths[column] || this.DEFAULT_COL_WIDTH;
    } else {
      this.preCollapseWidths[column] = this.columnWidths[column] || this.DEFAULT_COL_WIDTH;
      this.collapsedColumns.add(column);
      this.columnWidths[column] = this.getCollapsedColumnWidth();
    }
  }

  isColumnCollapsed(column: string): boolean {
    return this.collapsedColumns.has(column);
  }

  toggleGroupCollapse(group: TableHeaderCell, event: MouseEvent): void {
    event.stopPropagation();
    if (!group.groupId) return;
    if (this.collapsedGroups.has(group.groupId)) {
      this.collapsedGroups.delete(group.groupId);
      group.columns.forEach((col) => {
        this.collapsedColumns.delete(col);
        this.columnWidths[col] = this.preCollapseWidths[col] || this.DEFAULT_COL_WIDTH;
      });
    } else {
      this.collapsedGroups.add(group.groupId);
      group.columns.forEach((col) => {
        this.preCollapseWidths[col] = this.columnWidths[col] || this.DEFAULT_COL_WIDTH;
        this.collapsedColumns.add(col);
        this.columnWidths[col] = this.getCollapsedColumnWidth();
      });
    }
  }

  isGroupCollapsed(group: TableHeaderCell): boolean {
    if (!group.groupId || !group.columns?.length) return false;
    return this.collapsedGroups.has(group.groupId);
  }

  /**
   * Get the match mode from config
   */
  protected getMatchMode(): MatchMode {
    return this.data?.categoryMatchMode || 'contains';
  }

  // Emit filter based on selected headers
  // Sends normalized column names (e.g. "Q1 2025") so charts and tables match consistently
  protected emitHeaderFilter(): void {
    const filterValues: string[] = [];

    // For group headers, expand to all normalized column names under the group
    // e.g. clicking "2025" → sends ["Q1 2025", "Q2 2025", "Q3 2025", "Q4 2025"]
    this.selectedGroupHeaders.forEach((groupId) => {
      const group = this.getHeaderGroupNode(groupId);
      if (group?.columns) {
        group.columns.forEach((col) => {
          const normalizedCol = col.replace(/_/g, ' ');
          if (!filterValues.includes(normalizedCol)) {
            filterValues.push(normalizedCol);
          }
        });
      }
    });

    // For column headers, send normalized column key
    // e.g. clicking "Q1_2025" header → sends ["Q1 2025"]
    this.selectedColumnHeaders.forEach((column) => {
      const normalizedCol = column.replace(/_/g, ' ');
      if (!filterValues.includes(normalizedCol)) {
        filterValues.push(normalizedCol);
      }
    });

    if (filterValues.length === 0) {
      this.emitLegendFilter([]);
    } else {
      this.emitLegendFilter(filterValues);
    }
  }

  // Emit legend filter to other widgets (charts and tables)
  protected emitLegendFilter(selectedLegends: string[]): void {
    const filterListeners = this.resolveFilterListeners(this.data);
    if (filterListeners.length === 0) return;

    const widgetId = this.data?.widgetId;
    if (!widgetId) return;

    // Determine if this is a category-based filter (year/quarter headers)
    const isCategoryFilter =
      this.selectedGroupHeaders.size > 0 || this.selectedColumnHeaders.size > 0;

    // Use utility for category-based filter
    if (isCategoryFilter) {
      const labelKey = this.data?.labelKey || this.data?.legendKey || this.data?.filterKey;
      const listenerStr = filterListeners.join(',');

      // Set filter values in requestParams so listening charts include them in API payload
      if (selectedLegends.length > 0) {
        // Set the primary labelKey with individual categories (e.g., ["Q1 2025", "Q2 2025"])
        if (labelKey) {
          this.globalFilterService.updateFilterSelection(
            labelKey,
            selectedLegends,
            false,
            widgetId,
            false, // don't emit yet — we emit manually below
            listenerStr,
          );
        }

        // For group header clicks, also set doublexAxisKey with the group values (e.g., "2025")
        if (this.selectedGroupHeaders.size > 0 && this.data?.doublexAxisKey) {
          const groupValues = Array.from(this.selectedGroupHeaders)
            .map((groupId) => this.getHeaderGroupNode(groupId)?.label)
            .filter(Boolean) as string[];
          this.globalFilterService.updateFilterSelection(
            this.data.doublexAxisKey,
            groupValues,
            false,
            widgetId,
            false,
            listenerStr,
          );
        }
      } else {
        // Deselection: clear the filter keys
        if (labelKey) {
          this.globalFilterService.deleteKey(labelKey, false);
        }
        if (this.data?.doublexAxisKey) {
          this.globalFilterService.deleteKey(this.data.doublexAxisKey, false);
        }
      }

      const filterData = buildCategoryFilterData(selectedLegends, widgetId, filterListeners, labelKey);

      // Attach requestParams so listening charts send them in API payload
      const requestParams: Record<string, any> = {};
      if (selectedLegends.length > 0) {
        if (labelKey) {
          requestParams[labelKey] = selectedLegends;
        }
        if (this.selectedGroupHeaders.size > 0 && this.data?.doublexAxisKey) {
          requestParams[this.data.doublexAxisKey] = Array.from(this.selectedGroupHeaders)
            .map((groupId) => this.getHeaderGroupNode(groupId)?.label)
            .filter(Boolean);
        }
      }
      (filterData as any).requestParams = requestParams;

      (this.globalFilterService as any).filterSubject.next(filterData);
    } else {
      // Regular cell-based filter
      const filterData = {
        legendSync: true,
        selectedLegends: selectedLegends,
        selectedIndices: [],
        selectedColors: [],
        seriesNames: [],
        sourceWidgetId: widgetId,
        filterListener: filterListeners.join(','),
        isCategoryFilter: false,
      };
      (this.globalFilterService as any).filterSubject.next(filterData);
    }
  }

  // Handle external legend filter events from charts
  protected handleExternalLegendFilter(event: {
    sourceWidgetId: string;
    selectedLegends: string[];
    selectedIndices: number[];
    selectedColors: string[];
    targetWidgetIds: string[];
    visitedWidgetIds: string[];
    isCategoryFilter?: boolean;
  }): void {
    const widgetId = this.data?.widgetId;

    // Check if this widget should respond to the filter
    if (!widgetId || !event.targetWidgetIds.includes(widgetId)) {
      return;
    }

    // Don't respond if already visited (prevents infinite loops)
    if (event.visitedWidgetIds.includes(widgetId)) {
      return;
    }

    // Clear any cell selection
    this.selectedCells = [];

    // Handle reset signal (empty legends = show all)
    if (event.selectedLegends.length === 0 && event.selectedIndices.length === 0) {
      this.activeLegendSyncFilter = null;
      this.dataSource = [...this.originalDataSource];
      this.selectedGroupHeaders.clear();
      this.selectedColumnHeaders.clear();
      this.pageIndex = 0;
      this.setPagedData();
      // this.paginatorComp?.updatePagination(this.dataSource.length, this.pageIndex, this.pageSize);
      return;
    }

    // Store the active filter so it can be re-applied after a data reload
    this.activeLegendSyncFilter = { ...event, isCategoryFilter: event.isCategoryFilter || false };

    // Clear any previous header highlights
    this.selectedGroupHeaders.clear();
    this.selectedColumnHeaders.clear();

    const matchMode = this.getMatchMode();

    // Check if this is a category-based filter (from table header clicks like "2023", "Q4")
    if (event.isCategoryFilter) {
      // Match columns using normalized key (e.g. "Q1_2025" → "Q1 2025") with exact mode
      // to avoid false positives where short labels like "Q1" match "Q1 2025" via contains
      const matchingColumns = this.displayedColumns.filter((col) => {
        const normalizedCol = col.replace(/_/g, ' ');
        return event.selectedLegends.some(
          (legend) =>
            matchValue(col, legend, 'exact') ||
            matchValue(normalizedCol, legend, 'exact'),
        );
      });

      // Auto-select group header only when ALL its columns are matched
      // e.g. clicking year "2025" sends Q1-Q4 2025 → all 4 columns match → highlight "2025" group
      // but clicking "Q1 2025" sends only 1 → not all columns → don't highlight "2025" group
      for (const group of this.collectHeaderGroupNodes(this.headerGroups)) {
        if (!group.label || !group.columns?.length) continue;
        const allColumnsMatched = group.columns.every((col) => matchingColumns.includes(col));
        if (allColumnsMatched) {
          this.selectedGroupHeaders.add(group.id);
        }
      }

      if (matchingColumns.length > 0) {
        matchingColumns.forEach((col) => this.selectedColumnHeaders.add(col));
        // Don't filter rows for category-based selection, just highlight headers
        return;
      }
    }

    // Filter table data based on selected legends using crossFilterColumns config
    const crossFilterColumns: string[] = this.data?.crossFilterColumns || [];

    const filteredData = this.originalDataSource.filter((row: any) => {
      // Get values from all configured crossFilterColumns
      const rowValues = crossFilterColumns.map((colKey) =>
        (row[colKey] || '').toString().trim().toUpperCase(),
      );

      // Check if any of the row's crossFilterColumn values match any of the selected legends
      return event.selectedLegends.some((legend) => {
        const legendUpper = legend.trim().toUpperCase();
        return rowValues.includes(legendUpper);
      });
    });

    // Update dataSource with filtered data (or show all if no matches)
    this.dataSource = filteredData.length > 0 ? filteredData : [...this.originalDataSource];
    this.pageIndex = 0;
    this.setPagedData();
    // this.paginatorComp?.updatePagination(this.dataSource.length, this.pageIndex, this.pageSize);
  }

  // Clears all cell/header highlight selection state
  protected clearAllSelections(): void {
    this.selectedCells = [];
    this.selectedGroupHeaders.clear();
    this.selectedColumnHeaders.clear();
  }

  // Returns true when the filter event represents a full reset (no active filters)
  protected isResetFilter(filters?: any): boolean {
    if (!filters) return true;
    const hasQuery = Object.keys(filters?.queryParams || {}).length > 0;
    const hasRequest = Object.keys(filters?.requestParams || {}).length > 0;
    return !hasQuery && !hasRequest;
  }

  // Load table data from mock JSON file (for POC - uses GET request)
  protected loadAndRenderChart(filters?: any): void {
    console.log('filters', filters);
    this.isLoading = true;

    // Clear highlight selections on global reset so highlighted cells don't linger
    if (this.isResetFilter(filters)) {
      this.clearAllSelections();
    }

    // 1. Inline Data
    if (this.data?.tableData) {
      this.initTableWithData(this.data.tableData);
      return;
    }

    // 2. Determine URL (API or Mock)
    const url = this.data?.apiUrl || this.data?.mockDataUrl;

    // 3. Data Fetching
    if (url) {
      // Determine method (POST for API, GET if .json)
      let method = 'post';
      if (this.data?.method) {
        method = this.data.method;
      } else if (url.endsWith('.json')) {
        method = 'get';
      }

      this.configService
        .getConfig(url, this.data, filters, method, true)
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe({
          next: (res: any) => this.processApiResponse(res),
          error: (err) => {
            this.isLoading = false;
            console.error('Error loading table data', err);
          },
        });
    } else {
      this.isLoading = false;
    }
  }


  protected processApiResponse(res: any): void {
    let tableData: any[] = [];

    // Set pageSize from config if provided
    if (this.data?.pageSize) {
      this.pageSize = this.data.pageSize;
    }

    console.log(
      '[Base GenericTableComponent] processApiResponse - widgetId:',
      this.widgetId,
      'raw response keys:',
      res ? Object.keys(res) : null,
    );

    this.applyResponseTableConfig(res);

    const dataKey = this.data?.dataKey;
    if (Array.isArray(res?.tableRows)) {
      tableData = res.tableRows;
    } else if (dataKey && Array.isArray(res?.[dataKey])) {
      tableData = res[dataKey];
    }
    // Handle new nested structure with pivoting
    else if (res?.customer) {
      const map = new Map<string, any>();

      res.customer.forEach((item: any) => {
        const key = `${item.customer_name}_${item.channel_type || ''}`;
        if (!map.has(key)) {
          map.set(key, {
            customer_name: item.customer_name,
            channel_type: item.channel_type,
            _total: 0,
          });
        }
        const row = map.get(key);

        if (item.year) {
          row[item.year] = this.formatCurrency(item.sumOfTotalSales);
          row._total += item.sumOfTotalSales || 0;
        }
      });

      tableData = Array.from(map.values()).map((row: any) => {
        row['TOTAL'] = this.formatCurrency(row._total);
        delete row._total;
        return row;
      });
    } else if (Array.isArray(res?.options)) {
      tableData = res.options;
    } else if (Array.isArray(res?.data)) {
      tableData = res.data;
    } else if (Array.isArray(res)) {
      tableData = res;
    } else {
      tableData = [];
    }

    this.isLoading = false;

    // Legacy/Specific Logic (Safe guard)
    if (tableData.length > 0 && 'proposed_option_price' in tableData[0]) {
      tableData = tableData.map((row: any) => ({
        ...row,
        difference: Number((row.proposed_option_price - row.current_option_price).toFixed(3)),
      }));
      tableData.sort((a: any, b: any) => b.difference - a.difference);
    }

    this.originalDataSource = tableData;
    this.dataSource = [...this.originalDataSource];

    if (!!this.data?.isPagination) {
      this.pageIndex = 0; // Reset page on new data
      this.setPagedData();
    }

    this.displayedColumns = this.getDisplayedColumnsFromResponse(this.data, tableData);
    // Per UI Standards (Point 4): headers in Capital Case unless overridden via data.textCase
    const headerCase = (this.data?.textCase || 'capital') as any;
    const labelMap = this.getLabelMapFromResponse(this.data);
    this.columnLabels = Object.fromEntries(
      this.displayedColumns.map((key) => [
        key,
        labelMap.get(key) || formatDisplayText(key, headerCase),
      ]),
    );

    this.initColumnWidths();

    console.log(
      '[Base GenericTableComponent] processApiResponse - widgetId:',
      this.widgetId,
      'displayedColumns:',
      this.displayedColumns,
      'headerColumns:',
      this.data?.headerColumns,
      'headerGroups:',
      this.data?.headerGroups,
    );

    this.initGroupedHeaders();

    // Re-apply active legendSync filter if data was reloaded while a filter was active
    if (this.activeLegendSyncFilter) {
      this.handleExternalLegendFilter(this.activeLegendSyncFilter);
    }

    setTimeout(() => {
      this.paginatorComp?.updatePagination(this.dataSource.length, this.pageIndex);
    }, 200);
  }


  protected formatCurrency(value: number): string {
    if (value === undefined || value === null) return '-';
    if (value >= 1000000000) return (value / 1000000000).toFixed(1) + 'B';
    if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
    if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
    return value.toFixed(1);

  }

  onPageChange(event: any) {
    // Accept generic event (some paginator implementations emit a native Event)
    // and defensively read pageIndex/pageSize fields when available.
    const pEvent: any = event as any;
    this.pageSize = pEvent?.pageSize ?? this.pageSize;
    this.pageIndex = pEvent?.pageIndex ?? this.pageIndex;
    this.setPagedData();
  }

  // ===== PAGINATION USING UTILITY =====
  protected setPagedData() {
    // Use paginate utility (1-indexed internally, so add 1 to pageIndex)
    this.pagedData = paginate(this.dataSource, this.pageIndex + 1, this.pageSize);
  }

  // ─── Column Resize ────────────────────────────────────────────────────────

  protected initColumnWidths(): void {
    const configured: { [key: string]: number } = this.data?.columnWidths || {};
    for (const col of this.displayedColumns || []) {
      if (this.columnWidths[col] === undefined) {
        this.columnWidths[col] = configured[col] ?? this.DEFAULT_COL_WIDTH;
      }
    }
  }

  protected readNumericConfig(keys: string[]): number | null {
    for (const key of keys) {
      const direct = Number(this.data?.[key]);
      if (Number.isFinite(direct)) {
        return direct;
      }
      const nested = Number(this.data?.chartConfig?.[key]);
      if (Number.isFinite(nested)) {
        return nested;
      }
    }
    return null;
  }

  protected getMinColumnWidth(): number {
    const configuredMinWidth = this.readNumericConfig([
      'minColumnWidth',
      'minimumColumnWidth',
      'minColWidth',
      'minWidth',
    ]);
    if (configuredMinWidth !== null) {
      return Math.max(0, configuredMinWidth);
    }
    return this.MIN_COL_WIDTH;
  }

  protected getCollapsedColumnWidth(): number {
    const configuredCollapsedWidth = this.readNumericConfig([
      'collapsedColumnWidth',
      'collapseColumnWidth',
      'collapsedColWidth',
      'collapsedWidth',
    ]);
    if (configuredCollapsedWidth !== null) {
      return Math.max(this.getMinColumnWidth(), configuredCollapsedWidth);
    }
    return Math.max(this.getMinColumnWidth(), this.DEFAULT_COLLAPSED_COL_WIDTH);
  }

  // ─── Freeze Column Helpers (delegated to shared utility) ────────────────────

  isFrozenColumn(column: string): boolean {
    return _isFrozenColumn(this.data, this.displayedColumns, column);
  }

  isLastFrozenColumn(column: string): boolean {
    return _isLastFrozenColumn(this.data, this.displayedColumns, column);
  }

  getFrozenColumnLeft(column: string): number | null {
    return _getFrozenColumnLeft(this.data, this.displayedColumns, this.columnWidths, this.DEFAULT_COL_WIDTH, column);
  }

  isFrozenGroup(group: { columns?: string[] } | null | undefined): boolean {
    if (!group?.columns?.length) return false;
    return _isFrozenGroup(this.data, this.displayedColumns, group);
  }

  isFirstFrozenGroup(group: { columns?: string[] } | null | undefined): boolean {
    if (!group?.columns?.length) return false;
    return _isFirstFrozenGroup(this.data, this.displayedColumns, group);
  }

  getFrozenGroupLeft(group: { columns?: string[] } | null | undefined): number | null {
    if (!group?.columns?.length) return null;
    return _getFrozenGroupLeft(this.data, this.displayedColumns, this.columnWidths, this.DEFAULT_COL_WIDTH, group);
  }

  isFrozenBodyRow(rowIndex: number): boolean {
    const rowCount = this.data?.isPagination ? this.pagedData.length : this.dataSource.length;
    return _isFrozenBodyRow(this.data, rowIndex, rowCount);
  }

  getFrozenBodyRowTop(rowIndex: number): number | null {
    const rowCount = this.data?.isPagination ? this.pagedData.length : this.dataSource.length;
    return _getFrozenBodyRowTop(this.data, rowIndex, rowCount);
  }

  getFrozenBodyRowBottom(rowIndex: number): number | null {
    const rowCount = this.data?.isPagination ? this.pagedData.length : this.dataSource.length;
    return _getFrozenBodyRowBottom(this.data, rowIndex, rowCount);
  }

  onResizeStart(event: MouseEvent, column: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.suppressNextHeaderClick = true;
    this.resizingCol = column;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.columnWidths[column] ?? this.DEFAULT_COL_WIDTH;
    this.attachResizeListeners();
  }

  onHeaderMouseDown(event: MouseEvent, column: string): void {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const distanceFromRightEdge = rect.right - event.clientX;

    if (distanceFromRightEdge <= 16) {
      this.onResizeStart(event, column);
    }
  }

  protected attachResizeListeners(): void {
    if (typeof document === 'undefined') return;
    document.addEventListener('mousemove', this.documentMouseMoveHandler);
    document.addEventListener('mouseup', this.documentMouseUpHandler);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  protected detachResizeListeners(): void {
    if (typeof document === 'undefined') return;
    document.removeEventListener('mousemove', this.documentMouseMoveHandler);
    document.removeEventListener('mouseup', this.documentMouseUpHandler);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.resizingCol) return;
    const delta = event.clientX - this.resizeStartX;
    const newWidth = Math.max(this.getMinColumnWidth(), this.resizeStartWidth + delta);
    this.columnWidths[this.resizingCol] = newWidth;
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.resizingCol = null;
    this.detachResizeListeners();
    setTimeout(() => {
      this.suppressNextHeaderClick = false;
    }, 0);
  }

  filterByLegend(legendType: string | null): void {
    console.log('Clicked legendType:', legendType);
    console.log('Active legend before click:', this.activeLegend);

    if (this.activeLegend === legendType) {
      console.log('Same legend clicked again. Resetting filter.');
      this.activeLegend = null;
      this.dataSource = [...this.originalDataSource];
    } else {
      this.activeLegend = legendType;
      if (legendType) {
        console.log('Filtering data for legendType:', legendType);
        const filteredData = this.originalDataSource.filter(
          (row) =>
            (row.option_impact_drivers || '').trim().toLowerCase() ===
            (legendType || '').trim().toLowerCase(),
        );

        console.log('Filtered data:', filteredData);
        this.dataSource = filteredData;
      } else {
        console.log('LegendType is null, resetting dataSource to original.');
        this.dataSource = [...this.originalDataSource];
      }
    }

    console.log('DataSource after filtering:', this.dataSource);
    this.pageIndex = 0;
    this.setPagedData();
    // this.paginatorComp?.updatePagination(this.dataSource.length, this.pageIndex, this.pageSize);
  }

  /**
   * Format a header label for display per UI Standards (Point 4).
   * Used by grouped headers in the template.
   */
  formatHeaderLabel(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatDisplayText(value, mode);
  }

  /**
   * Determine if a vertical partition line should be shown between this column
   * and the next one. Per UI Standards (Point 5):
   *
   *   data.showColumnDividers = true                    → show on ALL columns
   *   data.showColumnDividers = false (default)         → no dividers
   *   data.dividerColumns = ['col1', 'col3']            → show only after these columns
   *
   * If both are provided, `dividerColumns` wins (selective mode).
   *
   * @param column - current column key being rendered
   */
  showDivider(column: string): boolean {
    const selective = this.data?.dividerColumns;
    if (Array.isArray(selective) && selective.length > 0) {
      return selective.includes(column);
    }
    return this.data?.showColumnDividers === true;
  }

  ngOnDestroy(): void {
    this.detachResizeListeners();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.legendFilterSub?.unsubscribe();
  }
}
